import { Box, SxProps, Theme, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import { MAX_IMAGE_SIZE_BYTES } from '../../../../constants/upload';

interface IFileUploaderBaseProps {
  accept?: string | string[];
  maxSize?: number;
  multiple?: boolean;
  onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  onDropRejected?: (fileRejections: FileRejection[]) => void;
  sx?: SxProps<Theme>;
  children: (props: { isDragActive: boolean; isDragReject: boolean; open: () => void }) => React.ReactNode;
  dataAction?: string;
}

export const FileUploaderBase: React.FC<IFileUploaderBaseProps> = ({
  accept,
  maxSize = MAX_IMAGE_SIZE_BYTES,
  multiple = false,
  onDrop,
  onDropRejected,
  sx,
  children,
  dataAction = 'file',
}) => {
  const theme = useTheme();

  const handleDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length && onDropRejected) {
        onDropRejected(fileRejections);
      }
      if (acceptedFiles.length && onDrop) {
        onDrop(acceptedFiles, fileRejections);
      }
    },
    [onDrop, onDropRejected],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    accept,
    maxSize,
    multiple,
    onDrop: handleDrop,
  });

  const uploaderContainerStyles: SxProps<Theme> = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 1,
    borderColor: theme.palette.divider,
    borderStyle: 'dashed',
    borderRadius: 0.5,
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease',
    overflow: 'hidden',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    ...sx,
  };

  return (
    <Box {...getRootProps()} sx={uploaderContainerStyles} data-action={`${dataAction}_uploader_container`}>
      <input {...getInputProps()} />
      {children({
        isDragActive,
        isDragReject,
        open,
      })}
    </Box>
  );
};
